import { bootstrapApplication } from '@angular/platform-browser';
import { createAppConfig } from './app/app.config';
import { AppComponent } from './app/app.component';
import { IAppSettings } from '@lb/config/interfaces/iapp-settings.interface';
import { Environments } from '@lb/enums/environment.enum';
import { enableProdMode, provideZoneChangeDetection } from '@angular/core';
import { AuthenticationService } from '@lb/auth/services/authentication.service';
import { captureImpersonationTargetFromUrl } from '@lb/auth/impersonation/impersonation-context';

fetch('assets/app-settings.json')
	.then((response) => response.json())
	.then(async (config: IAppSettings) => {
		if (config.env === Environments.PROD) {
			enableProdMode();
		}

		const authService: AuthenticationService = new AuthenticationService(config);

		// Allow unauthenticated access to the onboarding page — the flow is
		// gated by a signed JWT token in the URL, not an Entra session.
		const isOnboardingRoute = window.location.pathname.startsWith('/onboard');

		if (!isOnboardingRoute && !(await authService.isAuthenticated())) {
			const loginHint = new URLSearchParams(window.location.search).get('login_hint') ?? undefined;
			await authService.redirectToAuthorize(loginHint);
			return;
		}


		// Before the first authenticated call, and before Angular exists.
		//
		// The entry link carries ?userID= (or the older ?learnerID= / ?instructorID=), and the fetch below is what
		// decides who this tab believes it is — so the target has to be recorded first or that call
		// goes out as the admin. Capturing also strips the parameter from the URL, which is what
		// stops it re-triggering on later navigations, stops the link working when pasted into
		// somebody else’s tab, and keeps it out of analytics.
		//
		// In production all three apps share one origin, so a same-origin window.open hands this tab
		// a COPY of the admin’s sessionStorage — including their cached account object. Clearing it
		// is what forces the profile to be refetched under the new identity. In staging each app is
		// its own host, so no copy happens and the tab bootstraps from the shared cookie instead:
		// same flow, two behaviours, and a bug in either is invisible in the other.
		if (captureImpersonationTargetFromUrl() !== null) {
			authService.clearUser();
		}

		// Load user profile but don't fail if missing - let route guard handle it.
		// Skip for onboarding: no session exists and the route doesn't need one.
		if (!isOnboardingRoute) {
			await authService.init().catch(() => {
				console.log('User profile not found - will redirect to account issue page');
			});
		}

		const appConfig = createAppConfig(config);
		bootstrapApplication(AppComponent, { ...appConfig, providers: [provideZoneChangeDetection(), ...appConfig.providers] }).catch((err) =>
			console.error(err)
		);
	});
